/**
 * AccountView — self-service account screen for any signed-in user.
 *
 * Reached via the "Account" link in the top nav (both the organizer layout
 * header and the owner's Platform Admin header). Shows the user's identity and
 * lets them change their password (verifying the current one). On success the
 * session token is refreshed in place.
 *
 * It renders its own minimal chrome rather than reusing the organizer layout,
 * because the same view serves the owner (who has no organizer nav).
 */

import { apiService } from "../services/api";
import { t } from "../i18n/config";

type Account = { id: number; name: string; email: string; role: string; tenantId: string | null };

// Mirrors the server policy; validated client-side for instant feedback.
function validate(current: string, pw: string, confirm: string): string | null {
  if (!current) return t("account.validate.current");
  if (pw.length < 8) return t("account.validate.length");
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
    return t("account.validate.complexity");
  }
  if (pw !== confirm) return t("account.validate.mismatch");
  return null;
}

export class AccountView {
  private container: HTMLElement;
  private account: Account | null = null;
  private loadError = false;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  private get backHash(): string {
    return localStorage.getItem("userRole") === "owner" ? "#admin" : "#home";
  }

  async render(): Promise<void> {
    this.container.innerHTML = this.renderShell(`
      <div class="flex items-center justify-center gap-3 py-24 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
        <span class="w-5 h-5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" aria-hidden="true"></span> ${t("account.loading")}
      </div>
    `);

    try {
      this.account = await apiService.getAccount();
      this.loadError = false;
    } catch (error) {
      console.error("[Account] Failed to load account:", error);
      this.loadError = true;
    }

    this.container.innerHTML = this.renderShell(this.renderBody());
    this.setupListeners();
  }

  private renderShell(inner: string): string {
    return `
      <div class="min-h-screen bg-background-light dark:bg-background-dark daylight:bg-day-base">
        <header class="border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <a href="${this.backHash}" class="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink transition">
              <span class="material-symbols-outlined">arrow_back</span> ${t("account.back")}
            </a>
            <button id="account-logout-btn" class="btn-secondary text-sm border border-slate-400 daylight:border-day-ink-muted">
              <span class="material-symbols-outlined text-base">logout</span> ${t("nav.logout")}
            </button>
          </div>
        </header>
        <main class="max-w-3xl mx-auto px-4 py-8 space-y-8">${inner}</main>
      </div>
    `;
  }

  private renderBody(): string {
    if (this.loadError || !this.account) {
      return `
        <div class="card text-center py-16">
          <span class="material-symbols-outlined text-5xl text-error mb-3 block">error_outline</span>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t("account.loadError")}</p>
        </div>
      `;
    }

    const a = this.account;
    const roleLabel = a.role === "owner" ? t("account.roleOwner") : t("account.roleOrganizer");

    return `
      <div>
        <h1 class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t("nav.account")}</h1>
        <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1">${t("account.subtitle")}</p>
      </div>

      <section class="card">
        <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-4">${t("account.profile")}</h2>
        <dl class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <dt class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("account.field.name")}</dt>
            <dd class="font-semibold text-slate-900 dark:text-white daylight:text-day-ink mt-0.5">${this.escape(a.name)}</dd>
          </div>
          <div>
            <dt class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("account.field.email")}</dt>
            <dd class="font-semibold text-slate-900 dark:text-white daylight:text-day-ink mt-0.5">${this.escape(a.email)}</dd>
          </div>
          <div>
            <dt class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("account.field.role")}</dt>
            <dd class="font-semibold text-slate-900 dark:text-white daylight:text-day-ink mt-0.5">${roleLabel}</dd>
          </div>
        </dl>
      </section>

      <section class="card">
        <h2 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-1">${t("account.changePassword")}</h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-5">${t("account.passwordHint")}</p>

        <form id="account-password-form" class="space-y-4 max-w-md">
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">${t("account.currentPassword")}</label>
            <input type="password" id="account-current" required autocomplete="current-password" class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition" />
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">${t("account.newPassword")}</label>
            <input type="password" id="account-new" required autocomplete="new-password" class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition" />
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">${t("account.confirmPassword")}</label>
            <input type="password" id="account-confirm" required autocomplete="new-password" class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition" />
          </div>

          <div id="account-error" class="hidden bg-error/10 border border-error/30 text-error rounded-lg p-3 text-sm font-medium flex items-start gap-2">
            <span class="material-symbols-outlined flex-shrink-0 text-base">error</span>
            <span id="account-error-text"></span>
          </div>
          <div id="account-success" class="hidden bg-success/10 border border-success/30 text-success rounded-lg p-3 text-sm font-medium flex items-center gap-2">
            <span class="material-symbols-outlined flex-shrink-0 text-base">check_circle</span>
            <span>${t("account.success")}</span>
          </div>

          <button type="submit" id="account-submit" class="btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed">
            <span class="material-symbols-outlined">lock_reset</span><span>${t("account.updateBtn")}</span>
          </button>
        </form>
      </section>
    `;
  }

  private setupListeners(): void {
    this.container.querySelector("#account-logout-btn")?.addEventListener("click", () => {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("mustChangePassword");
      window.location.hash = "#login";
    });

    const form = this.container.querySelector("#account-password-form") as HTMLFormElement | null;
    if (!form) return;

    const errorDiv = this.container.querySelector("#account-error") as HTMLElement;
    const errorText = this.container.querySelector("#account-error-text") as HTMLElement;
    const successDiv = this.container.querySelector("#account-success") as HTMLElement;
    const submitBtn = this.container.querySelector("#account-submit") as HTMLButtonElement;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorDiv.classList.add("hidden");
      successDiv.classList.add("hidden");

      const current = (this.container.querySelector("#account-current") as HTMLInputElement).value;
      const next = (this.container.querySelector("#account-new") as HTMLInputElement).value;
      const confirm = (this.container.querySelector("#account-confirm") as HTMLInputElement).value;

      const validationError = validate(current, next, confirm);
      if (validationError) {
        errorText.textContent = validationError;
        errorDiv.classList.remove("hidden");
        return;
      }

      submitBtn.disabled = true;
      try {
        const response = await apiService.changePassword(current, next, confirm);
        if (response.token) localStorage.setItem("authToken", response.token);
        form.reset();
        successDiv.classList.remove("hidden");
      } catch (error) {
        console.error("[Account] Change password failed:", error);
        errorText.textContent = error instanceof Error ? error.message : t("account.changeFailed");
        errorDiv.classList.remove("hidden");
      } finally {
        submitBtn.disabled = false;
      }
    });
  }

  private escape(value: string): string {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
  }
}
